import React, { createContext, useContext, useReducer, useEffect } from 'react';

const ReturnsContext = createContext();

const initialState = {
  sellercloudsReturns: [],
  amazonReturns: [],
  loading: {
    sellercloud: false,
    amazon: false
  },
  pagination: {
    sellercloud: {
      page: 0,
      rowsPerPage: 25,
      totalCount: 0
    },
    amazon: {
      page: 0,
      rowsPerPage: 25,
      totalCount: 0
    }
  },
  filters: {
    search: '',
    statusFilter: ''
  },
  lastFetch: {
    sellercloud: null,
    amazon: null
  },
  cache: {
    sellercloud: new Map(),
    amazon: new Map()
  }
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const STORAGE_KEY = 'returns_cache';

function returnsReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.platform]: action.loading
        }
      };

    case 'SET_RETURNS':
      const cacheKey = `${action.platform}_${action.page}_${action.limit}_${action.search}_${action.status}`;
      return {
        ...state,
        [action.platform === 'sellercloud' ? 'sellercloudsReturns' : 'amazonReturns']: action.data,
        pagination: {
          ...state.pagination,
          [action.platform]: {
            page: action.page,
            rowsPerPage: action.limit,
            totalCount: action.totalCount
          }
        },
        lastFetch: {
          ...state.lastFetch,
          [action.platform]: Date.now()
        },
        cache: {
          ...state.cache,
          [action.platform]: new Map(state.cache[action.platform]).set(cacheKey, {
            data: action.data,
            totalCount: action.totalCount,
            timestamp: Date.now()
          })
        }
      };

    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: {
          ...state.pagination,
          [action.platform]: {
            ...state.pagination[action.platform],
            ...action.pagination
          }
        }
      };

    case 'SET_FILTERS':
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.filters
        }
      };

    case 'CLEAR_CACHE':
      return {
        ...state,
        cache: {
          sellercloud: new Map(),
          amazon: new Map()
        }
      };

    case 'RESTORE_FROM_STORAGE':
      return {
        ...state,
        ...action.data
      };

    default:
      return state;
  }
}

export function ReturnsProvider({ children }) {
  const [state, dispatch] = useReducer(returnsReducer, initialState);

  // Load cache from localStorage on mount
  useEffect(() => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        // Convert cache maps back from arrays
        if (parsed.cache) {
          parsed.cache.sellercloud = new Map(parsed.cache.sellercloud || []);
          parsed.cache.amazon = new Map(parsed.cache.amazon || []);
        }
        dispatch({ type: 'RESTORE_FROM_STORAGE', data: parsed });
      }
    } catch (error) {
      console.warn('Failed to load returns cache from localStorage:', error);
    }
  }, []);

  // Save cache to localStorage whenever it changes
  useEffect(() => {
    try {
      const toStore = {
        ...state,
        cache: {
          sellercloud: Array.from(state.cache.sellercloud.entries()),
          amazon: Array.from(state.cache.amazon.entries())
        }
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
    } catch (error) {
      console.warn('Failed to save returns cache to localStorage:', error);
    }
  }, [state]);

  const getCachedData = (platform, page, limit, search = '', status = '') => {
    const cacheKey = `${platform}_${page}_${limit}_${search}_${status}`;
    const cached = state.cache[platform].get(cacheKey);
    
    if (cached && (Date.now() - cached.timestamp < CACHE_DURATION)) {
      return cached;
    }
    return null;
  };

  const fetchReturns = async (platform, page = 0, limit = 25, search = '', status = '', forceRefresh = false, searchType = 'auto') => {
    // Check cache first unless forcing refresh
    if (!forceRefresh) {
      const cached = getCachedData(platform, page, limit, search, status);
      if (cached) {
        dispatch({
          type: 'SET_RETURNS',
          platform,
          data: cached.data,
          totalCount: cached.totalCount,
          page,
          limit
        });
        return cached.data;
      }
    }

    dispatch({ type: 'SET_LOADING', platform, loading: true });

    try {
      const params = new URLSearchParams({
        page: page + 1, // API expects 1-based indexing
        limit,
        fresh: forceRefresh ? 'true' : 'false'
      });
      
      if (search) params.append('search', search);
      if (status) params.append('status', status);
      if (platform !== 'amazon') params.append('platform', platform);
      if (platform === 'amazon' && searchType !== 'auto') params.append('searchType', searchType);

      const endpoint = platform === 'amazon' 
        ? `/api/returns/amazon?${params}` 
        : `/api/returns?${params}`;

      const sessionToken = localStorage.getItem('sessionToken');
      const response = await fetch(endpoint, {
        headers: {
          'Content-Type': 'application/json',
          ...(sessionToken && { 'Authorization': `Bearer ${sessionToken}` })
        }
      });
      const result = await response.json();

      if (result.success) {
        const data = platform === 'amazon' 
          ? result.data 
          : result.data.filter(item => 
              !(item.platform === 'Amazon Upload' || 
                item.platform === 'Amazon SP-API' || 
                item.platform === 'Amazon')
            );

        console.log(`🔍 Cache Debug - ${platform}:`, {
          totalReceived: result.data?.length || 0,
          afterFiltering: data.length,
          sampleItems: data.slice(0, 2).map(item => ({ id: item.id, platform: item.platform }))
        });

        dispatch({
          type: 'SET_RETURNS',
          platform,
          data,
          totalCount: platform === 'amazon' ? (result.pagination?.total || data.length) : data.length,
          page,
          limit,
          search,
          status
        });

        return data;
      } else {
        throw new Error(result.error || 'Failed to fetch returns');
      }
    } catch (error) {
      console.error(`Error fetching ${platform} returns:`, error);
      // Return cached data if available, even if expired
      const cached = getCachedData(platform, page, limit, search, status);
      if (cached) {
        return cached.data;
      }
      return [];
    } finally {
      dispatch({ type: 'SET_LOADING', platform, loading: false });
    }
  };

  const searchAllReturns = async (searchTerm, status = '', forceRefresh = false) => {
    // Fetch both Sellercloud and Amazon returns concurrently
    const [sellercloudsData, amazonData] = await Promise.all([
      fetchReturns('sellercloud', 0, 100, searchTerm, status, forceRefresh),
      fetchReturns('amazon', 0, 250, searchTerm, status, forceRefresh)
    ]);

    const results = {
      sellercloud: sellercloudsData || [],
      amazon: amazonData || [],
      orders: [] // Add orders array for order lookup results
    };

    // If no returns found, try searching for orders that could be used to create new RMAs
    const totalReturns = (results.sellercloud.length || 0) + (results.amazon.length || 0);
    if (totalReturns === 0) {
      console.log('🔍 No returns found, searching for orders to enable RMA creation...');
      try {
        const response = await fetch(`/api/orders/search?query=${encodeURIComponent(searchTerm)}`);
        if (response.ok) {
          const orderData = await response.json();
          if (orderData.success && orderData.data) {
            // Transform order data for display with "Create RMA" option
            const orderResults = [];
            
            // Add Amazon orders
            if (orderData.data.amazon && orderData.data.amazon.length > 0) {
              orderResults.push(...orderData.data.amazon.map(order => ({
                ...order,
                isOrderForRMA: true,
                platform: 'amazon',
                displayType: 'order'
              })));
            }
            
            // Add Sellercloud orders
            if (orderData.data.sellercloud && orderData.data.sellercloud.length > 0) {
              orderResults.push(...orderData.data.sellercloud.map(order => ({
                ...order,
                isOrderForRMA: true,
                platform: 'sellercloud',
                displayType: 'order'
              })));
            }
            
            results.orders = orderResults;
            console.log(`✅ Found ${orderResults.length} orders that can be used to create RMAs`);
          }
        }
      } catch (error) {
        console.error('Error searching for orders:', error);
      }
    }

    return results;
  };

  const updateReturnStatus = async (platform, returnId, newStatus) => {
    try {
      const dbId = returnId.replace('AMZ-DB-', '');
      const sessionToken = localStorage.getItem('sessionToken');
      const response = await fetch(`/api/returns/amazon/${dbId}/status`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          ...(sessionToken && { 'Authorization': `Bearer ${sessionToken}` })
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      const data = await response.json();
      if (data.success) {
        // Clear cache to force refresh
        dispatch({ type: 'CLEAR_CACHE' });
        // Refresh current data
        await fetchReturns(platform, state.pagination[platform].page, state.pagination[platform].rowsPerPage, state.filters.search, state.filters.statusFilter, true);
      }
      return data;
    } catch (error) {
      console.error('Error updating return status:', error);
      throw error;
    }
  };

  const deleteReturn = async (platform, returnId) => {
    try {
      const dbId = returnId.replace('AMZ-DB-', '');
      const sessionToken = localStorage.getItem('sessionToken');
      const response = await fetch(`/api/returns/amazon/${dbId}`, {
        method: 'DELETE',
        headers: { 
          'Content-Type': 'application/json',
          ...(sessionToken && { 'Authorization': `Bearer ${sessionToken}` })
        }
      });
      
      const data = await response.json();
      if (data.success) {
        // Clear cache to force refresh
        dispatch({ type: 'CLEAR_CACHE' });
        // Refresh current data
        await fetchReturns(platform, state.pagination[platform].page, state.pagination[platform].rowsPerPage, state.filters.search, state.filters.statusFilter, true);
      }
      return data;
    } catch (error) {
      console.error('Error deleting return:', error);
      throw error;
    }
  };

  const clearCache = () => {
    dispatch({ type: 'CLEAR_CACHE' });
    localStorage.removeItem(STORAGE_KEY);
  };

  const value = {
    ...state,
    fetchReturns,
    searchAllReturns,
    updateReturnStatus,
    deleteReturn,
    clearCache,
    getCachedData,
    setPagination: (platform, pagination) => dispatch({ type: 'SET_PAGINATION', platform, pagination }),
    setFilters: (filters) => dispatch({ type: 'SET_FILTERS', filters })
  };

  return (
    <ReturnsContext.Provider value={value}>
      {children}
    </ReturnsContext.Provider>
  );
}

export function useReturns() {
  const context = useContext(ReturnsContext);
  if (!context) {
    throw new Error('useReturns must be used within a ReturnsProvider');
  }
  return context;
}

export default ReturnsContext;
